import { useBoolean, useConst } from '@fluentui/react-hooks';
import { PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { PanelType } from 'office-ui-fabric-react/lib/Panel';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { Text } from 'office-ui-fabric-react/lib/Text';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Scriba from 'react-scriba';
import { NotificationsTarget, useNotificationsManager } from '../../contexts/notificationContext';
import PerimetreContext from '../../contexts/PerimetreContext';
import { Data } from '../../data/Data';
import { DataLotCommercial } from '../../data/DataLotCommercial.jsx';
import { DataMonCompte } from '../../data/DataMonCompte.jsx';
import { DataSouhait } from '../../data/DataSouhait.jsx';
import { MaxLengths } from '../../data/MaxLenghts.jsx';
import { Immocom } from '../../specifique/immocom';
import { ComboBoxCampagneCommerciale } from '../ComboBox/ComboBoxCampagneCommerciale';
import { ComboBoxDepartementAdministratif } from '../ComboBox/ComboBoxDepartementAdministratif.jsx';
import { ComboBoxMotifAcquisition } from '../ComboBox/ComboBoxMotifAcquisition';
import { ComboBoxNature } from '../ComboBox/ComboBoxNature.jsx';
import { ComboBoxNombrePiece } from '../ComboBox/ComboBoxNombrePiece';
import { ComboBoxOperation } from '../ComboBox/ComboBoxOperation.jsx';
import { ComboBoxOrigineCommerciale } from '../ComboBox/ComboBoxOrigineCommerciale';
import { ComboBoxProfilAcquereur } from '../ComboBox/ComboBoxProfilAcquereur';
import { ComboBoxRegionAdministrative } from '../ComboBox/ComboBoxRegionAdministrative.jsx';
import { ComboBoxSuiviCommercial } from '../ComboBox/ComboBoxSuiviCommercial';
import { ComboBoxVilleAdministrative } from '../ComboBox/ComboBoxVilleAdministrative.jsx';
import { EUICurrencyField } from '../EUI/EUICurrencyField';
import { EUIDatePicker } from '../EUI/EUIDatePicker.jsx';
import { EUIPanel } from '../EUI/EUIPanel';
import { EUITextField } from '../EUI/EUITextField.jsx';
import { EUIToggle } from '../EUI/EUIToggle';
import { HintVerticalPosition, HintWrapper } from '../Outils/HintWrapper.jsx';
import { FicheAlarmes } from './FicheAlarmes.jsx';
import './FicheSouhait.css';

const FicheSouhait = (props) => {

    // CONTEXTS
    const { clear, dismiss, extract, load } = useNotificationsManager();
    const { perimetre } = useContext(PerimetreContext);

    // PROPS
    const {
        idContact = undefined,
        idLotCommercial = undefined, // renseigné lorsqu'on arrive d'une réservation afin de définir la nature du souhait identique à celle du lotCommercial
        idOperation = undefined, // renseigné lorsqu'on arrive d'une réservation afin de définir la nature du souhait identique à celle du lotCommercial
        idSouhait = undefined,
        isOpen = false,
        /* --- */
        onDismiss = () => null,
        onSave = () => null
    } = props;

    // CONSTANTES
    const defaultSouhait = useConst({});

    // STATES
    const [_isFormValid, { setFalse: _setFormIsNotValid, setTrue: _setFormIsValid }] = useBoolean(false);

    const [_afficherPanelAlarmes, _setAfficherPanelAlarmes] = useState(false);
    const [_peutCreerSouhait, _setPeutCreerSouhait] = useState(false);
    const [_peutGererAlarmesSouhait, _setPeutGererAlarmesSouhait] = useState(false);
    const [_peutModifierPerimetre, _setPeutModifierPerimetre] = useState(true);
    const [_peutModifierSouhait, _setPeutModifierSouhait] = useState(false);
    const [_souhait, _setSouhait] = useState({ ...defaultSouhait });

    // REFS
    const _isLoaded = useRef(false); // Utilisé pour économiser des vérifications de formulaire
    const _isMounted = useRef(false); // Utilisé pour bloquer certains useEffect au premier render

    // MEMOS
    const _peutEditerSouhait = useMemo(() => {
        if (!!idSouhait) return _peutModifierSouhait;
        else return _peutCreerSouhait;
    }, [_peutCreerSouhait, _peutModifierSouhait, idSouhait]);

    // METHODS
    const _chargementDonnees = useCallback(() => {
        const id = load("Chargement du souhait en cours...");
        DataSouhait._chargementFicheSouhaitEnModeModification(idContact, idSouhait).then(response => {
            const onSuccess = () => {
                const data = response.reponseAPIobjet;
                const souhait = data[0];
                const peutCreerSouhait = data[1];
                const peutModifierSouhait = data[2];
                const peutModifierPerimetre = data[4];

                _isLoaded.current = true;
                _setSouhait(souhait);
                _setPeutCreerSouhait(peutCreerSouhait);
                _setPeutModifierSouhait(peutModifierSouhait);
                _setPeutModifierPerimetre(peutModifierPerimetre);
            };
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [idContact, idSouhait]);

    const _chargementDeLaNatureEtPerimetreDuLotCommercial = useCallback(() => {
        const id = load("Importation des informations du lot commercial en cours...");
        DataLotCommercial._chargementDeLaNatureEtPerimetreDuLotCommercial(idLotCommercial, idOperation).then(response => {
            const onSuccess = () => {
                const data = response.reponseAPIobjet;
                const idNature = data[0];
                const operation = data[1];
                const { idRegionAdministrative, idDepartementAdministratif, idVilleAdministrative, idOperation } = operation;
                _setSouhait(prev => ({
                    ...prev,
                    idRegionAdministrative, idDepartementAdministratif, idVilleAdministrative, idOperation,
                    rechercheIdNature: idNature,
                    rechercheIdOperation: idOperation
                }));
            };
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [idLotCommercial, idOperation]);

    const _chargementDroits = useCallback(() => {
        const id = load("Chargement des droits en cours...");
        DataSouhait._chargementFicheSouhaitEnModeCreation().then(response => {
            const onSuccess = () => {
                const droits = response.reponseAPIobjet;
                const peutCreerSouhait = droits[0];
                const peutModifierSouhait = droits[1];
                _setPeutCreerSouhait(peutCreerSouhait);
                _setPeutModifierSouhait(peutModifierSouhait);
            };
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, []);

    const _chargementDroitsLiesAuxAlarmes = useCallback(() => {
        const id = load("Chargement des droits en cours...");
        DataMonCompte._chargementDroitsLiesAuxAlarmes().then(response => {
            const onSuccess = () => {
                const droits = response.reponseAPIobjet;
                const peutGererAlarmesSouhait = droits.peutGererAlarmesSouhait;
                _setPeutGererAlarmesSouhait(peutGererAlarmesSouhait);
            };
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, []);

    const _onChangePerimetre = useCallback((key, value) => {
        _setSouhait(prev => {
            let updated = { ...prev, [key]: value };
            switch (key) {
                case "idRegionAdministrative": updated.idDepartementAdministratif = "0";
                case "idDepartementAdministratif": updated.idVilleAdministrative = "0";
                case "idVilleAdministrative": updated.rechercheIdOperation = 0;
                default: break;
            }
            return updated;
        });
    }, []);

    const _onDismiss = useCallback(() => {
        onDismiss();
    }, [onDismiss]);

    const _onDismissed = useCallback(() => {
        _isLoaded.current = false;
        _setFormIsNotValid();
        _setPeutCreerSouhait(false);
        _setPeutGererAlarmesSouhait(false);
        _setPeutModifierPerimetre(true);
        _setPeutModifierSouhait(false);
        _setSouhait(defaultSouhait);
    }, []);

    const _onOpen = useCallback(() => {
        if (!!idSouhait) _chargementDonnees();
        else {
            _chargementDroits();
            if (!!idOperation && !!idLotCommercial)
                _chargementDeLaNatureEtPerimetreDuLotCommercial();
            _isLoaded.current = true;
        }
        _chargementDroitsLiesAuxAlarmes();
    }, [idSouhait, perimetre]);

    const _onSave = useCallback(() => {
        const id = load("Enregistrement du souhait en cours...");
        DataSouhait._onEnregistrementSouhait(idContact, idSouhait, _souhait).then(response => {
            const onSuccess = onSave;
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [_souhait, idContact, idSouhait, onSave]);

    const _onVerificationFormulaire = useCallback(() => {
        _setFormIsNotValid();
        Data._onValidationFormulaire("Souhait", _souhait, idSouhait > 0 ? "Modification" : "Création").then(response => {
            const onSuccess = _setFormIsValid;
            const onError = (result) => extract(result, NotificationsTarget.panels);
            clear();
            Immocom.checkFetchResponse(response, onSuccess, onError);
        });
    }, [_souhait, idSouhait]);

    const _toggleUI = useCallback((nomUI, isOpen, callback) => () => {
        switch (nomUI) {
            case "PanelAlarmes": _setAfficherPanelAlarmes(isOpen); break;
            default: break;
        }
        if (!!callback) callback();
    }, []);

    // EFFECTS (ComponentDidUpdate)
    // Vérification des données du formulaire
    useEffect(() => {
        if (_isMounted.current && _isLoaded.current)
            _onVerificationFormulaire();
    }, [_isLoaded, _isMounted, _souhait]);

    // EFFECTS (ComponentDidMount)
    useEffect(() => {
        _isMounted.current = true;
    }, []);

    // UI
    const farButtons = useMemo(() => {
        if (!_peutGererAlarmesSouhait) return [];
        else {
            const button = <PrimaryButton key="btnPanelAlarmes" iconProps={{ iconName: "Ringer" }} onClick={_toggleUI("PanelAlarmes", true)} disabled={!idSouhait}>Gérer les alarmes</PrimaryButton>
            if (!!idSouhait) return [button];
            else return [
                <HintWrapper
                    content={<span>Le souhait doit être enregistré pour positionner des alarmes.</span>}
                    isVisibleOnHover
                    key="hintPanelAlarmes"
                    verticalAlign={HintVerticalPosition.center}
                >{button}</HintWrapper>
            ];
        }
    }, [_peutGererAlarmesSouhait, idSouhait]);

    // RENDER
    return <EUIPanel
        farButtons={farButtons}
        headerText={!!idSouhait ? "Modification de souhait" : "Nouveau souhait"}
        isFormValid={_isFormValid && _peutEditerSouhait}
        isOpen={isOpen}
        onDismiss={_onDismiss}
        onDismissed={_onDismissed}
        onOpen={_onOpen}
        onSave={_onSave}
        type={PanelType.extraLarge}
    >

        <Stack gap="10px">

            <Stack horizontal verticalAlign="center" gap="5px" className="ms-font-xl titreRubriqueFicheSouhait">
                <Icon iconName="Heart" />
                <span>Souhait</span>
            </Stack>
            <div className="ms-Grid">
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <ComboBoxProfilAcquereur
                            id="cbProfilAcquereur"
                            onChange={(event, option) => _setSouhait(prev => ({ ...prev, profilAcquereur: option.key }))}
                            selectedKey={_souhait.profilAcquereur}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <ComboBoxMotifAcquisition
                            id="cbMotifAcquisition"
                            onChange={(event, option) => _setSouhait(prev => ({ ...prev, rechercheMotifAcquisition: option.key }))}
                            required
                            selectedKey={_souhait.rechercheMotifAcquisition}
                        />
                    </div>
                </div>
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <ComboBoxNombrePiece
                            id="cbNombrePiece"
                            onChange={(event, option) => _setSouhait(prev => ({ ...prev, rechercheNombrePiece: option.key }))}
                            selectedKey={_souhait.rechercheNombrePiece}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <ComboBoxNature
                            disabled={!_peutModifierPerimetre}
                            id="cbNature"
                            label="Type de logement"
                            listIdASortir="11"
                            onLoad={(selectedItem, items) => {
                                const idNature = _souhait.rechercheIdNature;
                                if (!!idNature && !items.some(item => item.idNature === idNature)) // Check si idNature du souhait existe dans tableau des options
                                    _setSouhait(prev => ({ ...prev, rechercheIdNature: 0 })) // Si ce n'est pas le cas, réinitialiser idNature
                            }}
                            onChange={(event, option) => _setSouhait(prev => ({ ...prev, rechercheIdNature: option.key }))}
                            placeholder="Type de logement"
                            rangAffichage="1"
                            required
                            selectedKey={_souhait.rechercheIdNature}
                        />
                    </div>
                </div>
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <EUICurrencyField
                            id="tfBudgetAcquisition"
                            label="Budget acquisition"
                            onChange={_setFormIsNotValid}
                            onDelayedChange={(event, newValue) => _setSouhait(prev => ({ ...prev, rechercheBudgetAcquisition: newValue }))}
                            value={_souhait.rechercheBudgetAcquisition}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <EUICurrencyField
                            id="tfApportPersonnel"
                            label="Apport personnel"
                            onChange={_setFormIsNotValid}
                            onDelayedChange={(event, newValue) => _setSouhait(prev => ({ ...prev, rechercheApportPersonnel: newValue }))}
                            value={_souhait.rechercheApportPersonnel}
                        />
                    </div>
                </div>
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <ComboBoxSuiviCommercial
                            id="cbSuiviCommercial"
                            disabled={!!_souhait.idReservation}
                            onChange={(event, option) => _setSouhait(prev => ({ ...prev, suiviCommercial: option.key }))}
                            desactiverChampClient
                            selectedKey={(!!_souhait.idReservation ? "Client" : _souhait.suiviCommercial)}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <ComboBoxOrigineCommerciale
                            id="cbOrigineCommerciale"
                            onChange={(event, option) => _setSouhait(prev => ({ ...prev, rechercheOrigineCommerciale: option.key }))}
                            required
                            selectedKey={_souhait.rechercheOrigineCommerciale}
                        />
                    </div>
                </div>
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <EUIDatePicker
                            label="Date de dernier suivi"
                            onSelectDate={date => _setSouhait(prev => ({ ...prev, dateDernierSuiviCommercial: Scriba.date().getChaineISOFromDate(date) }))}
                            value={_souhait.dateDernierSuiviCommercial}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <ComboBoxCampagneCommerciale
                            onChange={(event, option) => _setSouhait(prev => ({ ...prev, rechercheCampagneCommerciale: option.key }))}
                            selectedKey={_souhait.rechercheCampagneCommerciale}
                        />
                    </div>
                </div>
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <EUIToggle
                            label="Eligible TVA réduite"
                            checked={_souhait.eligibleTVAReduite}
                            onChange={(e, isChecked) => _setSouhait(prev => ({ ...prev, eligibleTVAReduite: isChecked }))} />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-lg6">
                        <EUIToggle
                            label="Eligible PTZ"
                            checked={_souhait.eligiblePTZ}
                            onChange={(e, isChecked) => _setSouhait(prev => ({ ...prev, eligiblePTZ: isChecked }))} />
                    </div>
                </div>
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12 ms-lg6 ms-xl4">
                        <EUIDatePicker
                            label="Date RDV1"
                            onSelectDate={date => _setSouhait(prev => ({ ...prev, rechercheDateRDV1: Scriba.date().getChaineISOFromDate(date) }))}
                            value={_souhait.rechercheDateRDV1}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-lg6 ms-xl4">
                        <EUIDatePicker
                            label="Date RDV2"
                            onSelectDate={date => _setSouhait(prev => ({ ...prev, rechercheDateRDV2: Scriba.date().getChaineISOFromDate(date) }))}
                            value={_souhait.rechercheDateRDV2}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-lg6 ms-xl4">
                        <EUIDatePicker
                            label="Date RDV3"
                            onSelectDate={date => _setSouhait(prev => ({ ...prev, rechercheDateRDV3: Scriba.date().getChaineISOFromDate(date) }))}
                            value={_souhait.rechercheDateRDV3}
                        />
                    </div>
                </div>
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12">
                        <EUITextField
                            label="Commentaire commercial"
                            maxLength={MaxLengths.souhait.rechercheRemarque}
                            multiline
                            onChange={_setFormIsValid}
                            onDelayedChange={(event, newValue) => _setSouhait(prev => ({ ...prev, rechercheRemarque: newValue }))}
                            rows={6}
                            value={_souhait.rechercheRemarque}
                        />
                    </div>
                </div>
            </div>

            <Stack horizontal verticalAlign="center" gap="5px" className="ms-font-xl titreRubriqueFicheSouhait">
                <Icon iconName="World" />
                <span>Périmètre</span>
            </Stack>
            <div className="ms-Grid">
                <div className="ms-Grid-row perimetreSouhait">
                    <div className="ms-Grid-col ms-sm12">
                        <Text variant="Large" nowrap block>Périmètre <span className="asterixObligatoire">*</span></Text>
                    </div>
                    <div className="ms-Grid-col ms-sm12">
                        <EUIToggle
                            label="National"
                            checked={_souhait.nationalAdministratif}
                            onChange={(e, isChecked) => _setSouhait(prev => ({ ...prev, nationalAdministratif: isChecked, rechercheNational: isChecked }))}
                            disabled={!_peutModifierPerimetre}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl3">
                        <ComboBoxRegionAdministrative
                            disabled={_souhait.nationalAdministratif || !_peutModifierPerimetre}
                            id="cbRegionAdministrative"
                            onChange={(event, option) => _onChangePerimetre("idRegionAdministrative", option.key)}
                            selectedKey={_souhait.idRegionAdministrative}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl3">
                        <ComboBoxDepartementAdministratif
                            disabled={_souhait.nationalAdministratif || !_peutModifierPerimetre}
                            idRegionAdministrative={_souhait.idRegionAdministrative}
                            onChange={(event, option) => _onChangePerimetre("idDepartementAdministratif", option.key)}
                            selectedKey={_souhait.idDepartementAdministratif}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl3">
                        <ComboBoxVilleAdministrative
                            disabled={_souhait.nationalAdministratif || !_peutModifierPerimetre}
                            idDepartementAdministratif={_souhait.idDepartementAdministratif}
                            idRegionAdministrative={_souhait.idRegionAdministrative}
                            onChange={(event, option) => _onChangePerimetre("idVilleAdministrative", option.key)}
                            selectedKey={_souhait.idVilleAdministrative}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-xl3">
                        <ComboBoxOperation
                            disabled={_souhait.nationalAdministratif || !_peutModifierPerimetre}
                            idDepartementAdministratif={_souhait.idDepartementAdministratif}
                            idRegionAdministrative={_souhait.idRegionAdministrative}
                            idVilleAdministrative={_souhait.idVilleAdministrative}
                            onChange={(event, option) => _onChangePerimetre("rechercheIdOperation", option.key)}
                            perimetre="administratif"
                            selectedKey={_souhait.rechercheIdOperation || _souhait.idOperation}
                        />
                    </div>
                </div>
            </div>

        </Stack>

        <FicheAlarmes
            afficherAlarmesLieesASouhaits
            idContact={idContact}
            idSouhait={idSouhait}
            isOpen={_afficherPanelAlarmes}
            onDismiss={_toggleUI("PanelAlarmes", false)}
        />

    </EUIPanel>;

};

export default FicheSouhait;